/**
 * Toast Context
 *
 * Provides a global toast notification system accessible from any component.
 * Optionally scrolls to and pulses a field identified by data-field-id.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

export type ToastVariant = 'error' | 'success';

interface ToastContextValue {
  showToast: (message: string, fieldId?: string, variant?: ToastVariant) => void;
  toastMessage: string | null;
  toastVariant: ToastVariant;
  dismissToast: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVariant, setToastVariant] = useState<ToastVariant>('error');

  const showToast = useCallback((message: string, fieldId?: string, variant: ToastVariant = 'error') => {
    setToastMessage(null);
    setTimeout(() => {
      setToastVariant(variant);
      setToastMessage(message);

      if (fieldId) {
        setTimeout(() => {
          const el = document.querySelector(`[data-field-id="${fieldId}"]`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.remove('field-pulse');
            void (el as HTMLElement).offsetWidth;
            el.classList.add('field-pulse');
            const onEnd = () => { el.classList.remove('field-pulse'); el.removeEventListener('animationend', onEnd); };
            el.addEventListener('animationend', onEnd);
          }
        }, 100);
      }
    }, 10);
  }, []);

  const dismissToast = useCallback(() => setToastMessage(null), []);

  return (
    <ToastContext.Provider value={{ showToast, toastMessage, toastVariant, dismissToast }}>
      {children}
    </ToastContext.Provider>
  );
};

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
